import * as userAgent from '@play-co/user-agent';
import { analytics } from './analytics';
import type AnalyticsProperties from './analytics/AnalyticsProperties';
import * as events from './analytics/events';
import getQueryParameters from './getQueryParameters';
import { GameConfig, Platform } from './Platform';

/**
 * A stand-alone web platform for anonymous users.
 *
 * @example
 * ```ts
 * import { PlatformWeb } from '@play-co/gcinstant';
 *
 * // Initialize platform:
 * const platform = new PlatformWeb();
 * await platform.initializeAsync({ appID, version, ... });
 *
 * // Player ID must be set before game start. Use for example Replicant user ID:
 * platform.setPlayerID(replicant.userId);
 *
 * // Start game:
 * await platform.startGameAsync();
 * ```
 */
export class PlatformWeb extends Platform {
  constructor() {
    super('web');
  }

  async initializeAsync(config?: GameConfig): Promise<void> {
    if (this._initialized) {
      return Promise.resolve();
    }

    this.trackTimeTo('initializeAsync');
    this.readConfiguration(config);
    this.getEntryPointData();

    this._initialized = true;
    this.resolveFriendsAvailable();

    this.osType = userAgent.os.isAndroid
      ? 'ANDROID'
      : userAgent.os.isIOS
      ? 'IOS'
      : 'WEB';
    this.isMobile = this.osType !== 'WEB';

    analytics.initialize(
      undefined, // Don't set analytics user ID to keep user anonymous in Amplitude
      {
        ...this._gameConfig,
        appID: this.appID, // this is to work around years-old technical debt related to appID sometimes being an object in the config
      },
    );
    events.trackPlatformInitSuccess();
  }

  loadStorage(): Promise<void> {
    this.throwIfPlayerIDIsNotSet(); // Player ID is required after storage load to set up A/B tests

    return super.loadStorage();
  }

  /**
   * Set anonymous player ID, using for example Replicant user ID.
   *
   * This does not set the Amplitude user ID so that user remains anoymous in analytics.
   *
   * Must be called before `startGameAsync`.
   */
  setPlayerID(playerID: string): void {
    this.playerID = playerID;
    analytics.setUserProperties({ playerID });
  }

  setSessionData(): void {} // no-op

  protected async startGameAsyncInner(): Promise<void> {
    this.throwIfPlayerIDIsNotSet(); // Player ID is required before Entry analytics to set up A/B tests
    this.trackTimeTo('startGameAsync');

    await this._prepareEntryData();
  }

  protected _getEntryPointDataForce(): AnalyticsProperties.EntryData {
    try {
      const { payload } = getQueryParameters();
      if (payload) {
        return JSON.parse(payload);
      }
    } catch (error) {
      events.trackPayloadReadFailure(error, {
        rawPayload: window.location.search,
        type: 'parseFailed',
      });
    }

    return {} as AnalyticsProperties.EntryData;
  }

  private throwIfPlayerIDIsNotSet(): void {
    if (!this.playerID) {
      throw Error(
        'Player ID not defined, game should call Platform.setPlayerID first',
      );
    }
  }
}
